import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';

import { useCloseClaim } from '@common/slices';
import { IBaseDrawerProps, ICloseClaimValues } from '@common/types';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';

interface Props extends IBaseDrawerProps {
  claimId: string;
  claimStatus?: string;
}

const getInitialValues = (): ICloseClaimValues => ({
  closureReason: ''
});

const CloseClaimDrawer: React.FC<Props> = ({
  claimId,
  claimStatus,
  onSuccess
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const [values, setValues] = useState<ICloseClaimValues>(getInitialValues());
  const [closeClaim] = useCloseClaim();

  const sections: IDynamicFormWidgetSection[] = useMemo(
    () => [
      {
        title: 'Close Claim',
        inputs: {
          closureReason: {
            name: 'closureReason',
            title: 'Reason',
            type: FormFieldType.textArea,
            placeholder: 'Enter reason',
            value: values.closureReason,
            required: true,
            disabled: false,
            maxLength: 1000
          }
        }
      }
    ],
    [values.closureReason]
  );

  const handleSubmit = async () => {
    try {
      if ((claimStatus ?? 'New').toLowerCase() !== 'new') {
        throw new Error(
          'Claim estimations closed as claim&apos;s status is different than'
        );
      }

      const closureReason = values.closureReason.trim();
      if (!closureReason) {
        throw new Error('Please enter a reason');
      }

      const res = await closeClaim({
        CloseClaimInput: {
          claimId,
          closureReason
        }
      });
      if (!res.errors) {
        onSuccess();
      }
    } catch (error) {
      console.error(error);
      toast.error(<ToastErrorMessage>{error.message}</ToastErrorMessage>);
    }
  };

  const handleChange = (
    fieldName: keyof ICloseClaimValues,
    value: any,
    _sectionIndex: number,
    updatedValues: ICloseClaimValues
  ) => {
    const newValues = { ...updatedValues, [fieldName]: value };
    setValues(newValues);
  };

  // Reset form when drawer is opened (via parent component)
  useEffect(() => {
    setValues(getInitialValues());
  }, []);

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={handleSubmit}
      onChange={handleChange}
      submitButtonState={submitButtonState}
    />
  );
};

export { CloseClaimDrawer };
